// apps/account-portal/src/pages/BillingHistoryPage.tsx
import React, { useEffect, useContext } from 'react';
import { UserContext } from '../context/UserContext';
import { Invoice } from '@redpanda/types';
import { Alert, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, StatusDot, type StatusTone } from '@redpanda/ui';
import { useTranslation } from 'react-i18next';
import { useSubscriptionStore } from '../stores/subscriptionStore';
import PageContainer from '../components/PageContainer';
import PageTitle from '../components/PageTitle';

// 帳單狀態 → 狀態點語意（R6）
const invoiceTone: Record<Invoice['status'], StatusTone> = {
    Paid: 'success',
    Failed: 'danger',
    Pending: 'warning',
};

const StatusBadge: React.FC<{ status: Invoice['status'] }> = ({ status }) => {
    const { t } = useTranslation();

    const statusText = {
        Paid: t('pages.billing.status.paid'),
        Failed: t('pages.billing.status.failed'),
        Pending: t('pages.billing.status.pending'),
    };

    return <StatusDot tone={invoiceTone[status]} label={statusText[status]} />;
};

const BillingHistoryPage: React.FC = () => {
    const userContext = useContext(UserContext);
    const { t } = useTranslation();

    // Use zustand store instead of local state
    const {
        systemInvoices: invoices,
        systemInvoicesLoaded: invoicesLoaded,
        systemInvoicesError: invoicesError,
        isLoadingInvoices,
        fetchSystemInvoices: fetchInvoices,
    } = useSubscriptionStore();

    // 金額：幣別＋千分位
    const getLocalizedPrice = (amount: number) => {
        const currency = t('pricing.currency', { ns: 'staticContent' });
        return `${currency}${amount.toLocaleString()}`;
    };

    // Helper function to translate plan names based on system
    const translatePlanName = (planName: string, system: string) => {
        const systemKey = system?.toLowerCase();

        // 根據中文方案名稱和系統來翻譯
        if (planName === '免費版') {
            return t(`systems.${systemKey}.plans.free.name`, { ns: 'staticContent' });
        }
        if (planName === '基本版') {
            return t(`systems.${systemKey}.plans.basic.name`, { ns: 'staticContent' });
        }
        if (planName === '專業版') {
            return t(`systems.${systemKey}.plans.pro.name`, { ns: 'staticContent' });
        }

        // 後備翻譯 - 使用 translation.json 中的通用方案名稱
        if (planName === '免費版') return t('pages.subscription.plan_free');
        if (planName === '基本版') return t('pages.subscription.plan_basic');
        if (planName === '專業版') return t('pages.subscription.plan_pro');

        // 如果都找不到，返回原名稱
        return planName;
    };

    // Handle data fetching
    useEffect(() => {
        if (!userContext?.isLoading && userContext?.isAuthenticated) {
            fetchInvoices();
        }
    }, [userContext?.isLoading, userContext?.isAuthenticated]);  // eslint-disable-line react-hooks/exhaustive-deps

    // 整欄都沒有 PDF 時不顯示「下載」欄（§3-2：不要一整欄「不適用」）
    const hasAnyPdf = invoices.some(invoice => !!invoice.pdfUrl);

    // If user is still loading, don't render anything to prevent flicker
    if (userContext?.isLoading) {
        return null;
    }

    return (
        <PageContainer>
            <PageTitle title={t('pages.billing.title')} />
            {invoicesError && <Alert variant="error" title={t('common.status.error')} className="mb-6">{invoicesError}</Alert>}
            <div className="bg-[var(--surface)] border border-[var(--border-c)] rounded-lg overflow-hidden">
                {isLoadingInvoices || !invoicesLoaded ? (
                    <div className="space-y-3 p-5">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="animate-pulse flex gap-4 py-3 px-2 border-b border-[var(--border-c)] last:border-b-0">
                                <div className="h-4 bg-[var(--page-bg)] rounded w-1/6"></div>
                                <div className="h-4 bg-[var(--page-bg)] rounded w-1/6"></div>
                                <div className="h-4 bg-[var(--page-bg)] rounded w-1/4"></div>
                                <div className="h-4 bg-[var(--page-bg)] rounded w-1/8"></div>
                                <div className="h-4 bg-[var(--page-bg)] rounded w-1/6"></div>
                                <div className="h-4 bg-[var(--page-bg)] rounded w-1/8"></div>
                            </div>
                        ))}
                    </div>
                ) :!invoicesError && invoices.length > 0 ? (
                    <div className="overflow-x-auto">
                    <Table style={{ tableLayout: 'fixed', minWidth: '720px' }}>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-36">{t('pages.billing.table.invoice_id')}</TableHead>
                                <TableHead className="w-32">{t('pages.billing.table.date')}</TableHead>
                                <TableHead>{t('pages.billing.table.plan')}</TableHead>
                                <TableHead numeric className="w-32">{t('pages.billing.table.amount')}</TableHead>
                                <TableHead className="w-28">{t('pages.billing.table.status')}</TableHead>
                                {hasAnyPdf && <TableHead className="w-24">{t('pages.billing.table.download')}</TableHead>}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {invoices.map((invoice) => (
                                <TableRow key={invoice.id}>
                                    <TableCell className="tabular-nums text-[var(--text-secondary)]">{invoice.invoiceNumber}</TableCell>
                                    <TableCell className="tabular-nums">{new Date(invoice.date).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        {invoice.system ? (
                                            <>
                                                <span className="text-sm text-[var(--text-secondary)]">
                                                    {t(`systems.${invoice.system.toLowerCase()}.name`)}
                                                </span>
                                                <br />
                                                <span>{translatePlanName(invoice.planName, invoice.system)}</span>
                                            </>
                                        ) : (
                                            translatePlanName(invoice.planName, invoice.system || 'crm')
                                        )}
                                    </TableCell>
                                    <TableCell numeric>{getLocalizedPrice(invoice.amount)}</TableCell>
                                    <TableCell><StatusBadge status={invoice.status} /></TableCell>
                                    {hasAnyPdf && (
                                        <TableCell>
                                            {invoice.pdfUrl ? (
                                                <a href={invoice.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] hover:underline">
                                                    {t('pages.billing.table.download')}
                                                </a>
                                            ) : (
                                                <span className="text-[var(--text-muted)]">—</span>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    </div>
                ) : (
                    <div className="p-12 text-center">
                        <p className="text-[var(--text-secondary)]">{t('pages.billing.no_invoices')}</p>
                    </div>
                )}
            </div>
        </PageContainer>
    );
};

export default BillingHistoryPage;
